import React, { useState } from "react";
import { Box, ButtonBase } from "@mui/material";
import WindowIcon from "@mui/icons-material/Window";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import NotificationsIcon from "@mui/icons-material/Notifications";

// Color variant switch. false = the light bar as-is. true = the dark variant
// (dark-gray surface, gray gradient border, gray inactive icons, white active
// pill with a blue-to-green active glyph). Flip manually to preview the variant.
const DARK = true;

// Icons mirror the top navbar so the two surfaces stay visually consistent. The
// glyph size is set per-instance (see `compact`) so it can render small.
const TABS = [
  { key: "notifications", label: "Notifications", Icon: NotificationsIcon },
  { key: "dashboard", label: "Dashboard", Icon: WindowIcon },
  { key: "messages", label: "Messages", Icon: ChatBubbleIcon },
];

// Two size sets. Compact (desktop) wraps tight around a 24px icon to match the
// account icon next to it; the default (mobile) keeps the larger, tappable bar
// but matches the same icon-to-tab ratio so the glyph padding looks identical.
const COMPACT_SIZES = {
  tab: 40,
  icon: 24,
  badgeOffset: 0,
  badgeSize: 15,
  badgeFont: 9,
};
const DEFAULT_SIZES = {
  tab: 60,
  icon: 36,
  badgeOffset: 9,
  badgeSize: 18,
  badgeFont: 11,
};

// Small breathing room between the three icons (theme spacing 1 = 8px).
const GAP = 8;

// Active "pill" colour: a flat primary fill, anchored on the v2 MUI theme
// primary (#0395D3).
const ACTIVE_MAIN = "#0395D3"; // theme.palette.primary.main
// Inactive side icons. Light: a gray gradient (lighter top easing to #666
// bottom). Dark: a flat #888 gray (both stops equal).
const ICON_GRAY = DARK ? "#999999" : "#3F3F3F"; // bottom of the glyph
const ICON_GRAY_TOP = DARK ? "#999999" : "#6E6E6E"; // top of the glyph

// MUI icons are SVGs whose paths fill with currentColor, so we point their fill
// at an inline SVG gradient instead. The ids are referenced from the icon's
// `fill` (duplicate defs across instances resolve to the first, which is
// identical, so it is safe).
const ICON_GRADIENT_ID = "navActionBarIconGray";

// Active glyph over the pill. Light: pure white top easing to a light gray
// bottom. Dark: blue (#03A9F4) top easing to green (#4CAF50) bottom.
const ICON_ACTIVE_TOP = DARK ? "#03A9F4" : "#FFFFFF";
const ICON_ACTIVE_BOTTOM = DARK ? "#4CAF50" : "#D4DEE3";
const ICON_ACTIVE_GRADIENT_ID = "navActionBarIconWhite";

// Shared snippet: centre a flex container's children on both axes.
const FLEX_CENTER = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

// Raised surface face, translucent so the blurred page shows through. Light:
// white at ~45% alpha. Dark: dark gray at ~45% alpha. Kept low so the frosted
// content behind reads clearly rather than looking near-solid.
const SURFACE_BACKGROUND = DARK ? "rgba(0,0,0,0.65)" : "rgba(255,255,255,0.65)";
// 1px gradient border around the container, lighter top easing to a darker
// bottom. Painted by the outer wrapper's background under a 1px pad. Also
// translucent so the frosted blur reads through the border ring.
const BORDER_GRADIENT = DARK
  ? "linear-gradient(180deg,rgba(120,120,120,0.6) 0%,rgba(63,63,63,0.6) 100%)"
  : "linear-gradient(180deg,rgba(255,255,255,0.65) 0%,rgba(201,201,201,0.65) 100%)";
// Frosted-glass blur applied to both layers so scrolling content behind the
// bar reads as a soft blur. WebkitBackdropFilter is required for iOS Safari.
const SURFACE_BLUR = "blur(6px)";
// The outer drop shadow lifts the bar off the page when it floats (mobile),
// scaled to the theme's subtle #00000013 shadow language (neutral black, no blue
// tint).
const OUTER_SHADOW =
  "0 12px 28px -10px rgba(0,0,0,0.22),0 4px 10px -6px rgba(0,0,0,0.14)";

// The sliding pill: a flat fill, no gradient, gloss, or shadow. Light: primary
// blue. Dark: white.
const PILL_BACKGROUND = DARK ? "#FFFFFF" : ACTIVE_MAIN;

// Unread-count badge: warm red pill with a white ring so it reads on any tab.
const BADGE_BACKGROUND = "linear-gradient(180deg,#ff6a5a 0%,#e8412f 100%)";
const BADGE_SHADOW =
  "0 2px 5px rgba(180,30,20,0.45),inset 0 1px 1px rgba(255,255,255,0.45),0 0 0 2px rgba(255,255,255,0.85)";

const formatBadge = (count) => (count > 99 ? "99+" : String(count));

/**
 * NavActionBar
 * @param {number}   [value]          Controlled active index (0..2)
 * @param {number}   [defaultValue]   Uncontrolled initial index. Default 1.
 * @param {(index:number, tab:object)=>void} [onChange]
 * @param {number[]} [badges]         Per-tab counter; index matches TABS. 0/undefined hides it.
 * @param {boolean}  [floating]       Render the raised surface (rounded corners, gradient, shadows). When false the wrapper is transparent. Default true.
 * @param {boolean}  [compact]        Use the smaller (desktop) size set. Default false.
 */
export default function NavActionBar({
  value,
  defaultValue = 1,
  onChange,
  badges = [],
  floating = true,
  compact = false,
}) {
  const {
    tab: SIZE,
    icon: ICON_SIZE,
    badgeOffset: BADGE_OFFSET,
    badgeSize: BADGE_SIZE,
    badgeFont: BADGE_FONT,
  } = compact ? COMPACT_SIZES : DEFAULT_SIZES;
  const [internal, setInternal] = useState(defaultValue);
  const active = value != null ? value : internal;

  const handle = (i) => {
    if (value == null) setInternal(i);
    onChange?.(i, TABS[i]);
  };

  return (
    // Single surface: a flat translucent-black face (frosted by the backdrop
    // blur) with a 1px gradient stroke painted ONLY on the 1px ring. The stroke
    // can't be a wrapper-behind layer: a translucent fill stacked over a
    // gradient lets the gradient bleed through the whole face, tinting it. So
    // the gradient lives on a ::before whose interior is masked out, leaving the
    // border ring crisp and the face dead-flat. Shown in both modes; only the
    // raised shadow is exclusive to the floating mobile bar.
    <Box
      sx={{
        position: "relative",
        ...FLEX_CENTER,
        gap: `${GAP}px`,
        height: SIZE,
        borderRadius: `${SIZE / 2}px`,
        background: SURFACE_BACKGROUND,
        // Frosted blur over the translucent face so scrolling content reads as a
        // soft blur behind the bar. WebkitBackdropFilter is required for iOS.
        backdropFilter: SURFACE_BLUR,
        WebkitBackdropFilter: SURFACE_BLUR,
        boxShadow: floating ? OUTER_SHADOW : "none",
        // 1px gradient border, masked to the ring only. The gradient fills the
        // padding box; the mask XORs the content-box out so the interior stays
        // transparent and never tints the flat face behind it.
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "inherit",
          padding: "1px",
          background: BORDER_GRADIENT,
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          maskComposite: "exclude",
          pointerEvents: "none",
        },
      }}
    >
      {/* Gradient definition for the inactive gray icons. Zero-size, no layout. */}
      <Box
        component="svg"
        aria-hidden="true"
        sx={{ position: "absolute", width: 0, height: 0 }}
      >
        <defs>
          <linearGradient id={ICON_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={ICON_GRAY_TOP} />
            <stop offset="100%" stopColor={ICON_GRAY} />
          </linearGradient>
          <linearGradient
            id={ICON_ACTIVE_GRADIENT_ID}
            x1="0"
            y1="0"
            x2="0"
            y2="1"
          >
            <stop offset="0%" stopColor={ICON_ACTIVE_TOP} />
            <stop offset="100%" stopColor={ICON_ACTIVE_BOTTOM} />
          </linearGradient>
        </defs>
      </Box>

      {/* Sliding indicator: marks the active tab and glides to it when the user
          switches icons. The active icon tints white over the pill; the inactive
          icons stay grey. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          // Inset 1px on every side so the pill clears the gradient border ring.
          // Diameter shrinks by 2px and the offset gains 1px to stay centred on
          // the active icon (icon centre = active*(SIZE+GAP) + SIZE/2).
          top: "1px",
          left: `${active * (SIZE + GAP) + 1}px`,
          width: SIZE - 2,
          height: SIZE - 2,
          borderRadius: "50%",
          background: PILL_BACKGROUND,
          transition: "left 0.32s cubic-bezier(0.34,1.3,0.64,1)",
        }}
      />

      {TABS.map((tab, i) => {
        const isActive = i === active;
        const badgeCount = badges[i] ?? 0;
        const TabIcon = tab.Icon;
        return (
          <ButtonBase
            key={tab.key}
            disableRipple
            aria-label={
              badgeCount > 0 ? `${tab.label}, ${badgeCount} unread` : tab.label
            }
            aria-pressed={isActive}
            onClick={() => handle(i)}
            sx={{
              position: "relative",
              zIndex: 2,
              width: SIZE,
              height: SIZE,
              borderRadius: "50%",
              // The active icon rides its white pill; inactive icons stay grey
              // and pick up a subtle circular tint on hover (the MUI action
              // overlay idiom). Colour eases as the active tint follows the pill.
              color: isActive ? "#ffffff" : ICON_GRAY,
              transition: "background-color 0.2s ease, color 0.35s ease",
              "&:hover": {
                backgroundColor: isActive ? "transparent" : "rgba(0,0,0,0.045)",
              },
            }}
          >
            <TabIcon
              sx={{
                fontSize: ICON_SIZE,
                // Active icon rides the blue pill with a white-to-light-gray
                // gradient; inactive icons take the vertical gray gradient.
                fill: isActive
                  ? `url(#${ICON_ACTIVE_GRADIENT_ID})`
                  : `url(#${ICON_GRADIENT_ID})`,
              }}
            />

            {badgeCount > 0 && (
              <Box
                aria-hidden="true"
                sx={{
                  ...FLEX_CENTER,
                  position: "absolute",
                  top: `${BADGE_OFFSET}px`,
                  right: `${BADGE_OFFSET}px`,
                  minWidth: BADGE_SIZE,
                  height: BADGE_SIZE,
                  px: "5px",
                  boxSizing: "border-box",
                  borderRadius: `${BADGE_SIZE / 2}px`,
                  background: BADGE_BACKGROUND,
                  color: "#ffffff",
                  fontSize: BADGE_FONT,
                  fontWeight: 700,
                  lineHeight: 1,
                  boxShadow: BADGE_SHADOW,
                }}
              >
                {formatBadge(badgeCount)}
              </Box>
            )}
          </ButtonBase>
        );
      })}
    </Box>
  );
}
